export const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000/api');

const API_KEY = process.env.REACT_APP_API_KEY || '';
const getToken = () => localStorage.getItem('bookscape_token');
const setToken = (token) => localStorage.setItem('bookscape_token', token);

const buildHeaders = (sendJson) => {
  const h = {};
  if (sendJson) h['Content-Type'] = 'application/json';
  if (API_KEY) h['X-API-Key'] = API_KEY;
  const token = getToken();
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};

const handleRes = async (res) => {
  let data = {};
  try {
    data = await res.json();
  } catch {
    // non-JSON response (e.g. empty body)
  }
  if (!res.ok) {
    const err = new Error(data.message || `Request failed with status ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
};

// Thin fetch wrapper used by every domain file.
// Behaviour matches the original api.js helpers:
//   - Authorization header is attached whenever a token is present.
//   - Content-Type: application/json is only added when sending a body.
//   - On 401, attempts a silent refresh before failing.
//
//   request('/books')                                  → GET
//   request('/cart', { method: 'POST', body: {...} })  → POST with JSON body

let refreshPromise = null;

const attemptRefresh = async () => {
  // Deduplicate concurrent refresh attempts
  if (refreshPromise) return refreshPromise;
  refreshPromise = fetch(`${API_BASE}/auth/refresh-token`, {
    method: 'POST',
    credentials: 'include',
    headers: API_KEY ? { 'X-API-Key': API_KEY } : {},
  })
    .then(async (res) => {
      if (!res.ok) throw new Error('Refresh failed');
      const data = await res.json();
      setToken(data.token);
      return data.token;
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
};

export const request = async (path, { method = 'GET', body } = {}) => {
  const hadAccessToken = Boolean(getToken());
  let res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: buildHeaders(!!body),
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });

  // Refresh only a session that already had an access token. A refresh cookie
  // alone must not silently log a visitor in after local/session logout.
  if (res.status === 401 && hadAccessToken && path !== '/auth/refresh-token') {
    try {
      await attemptRefresh();
      // Retry with new token
      res = await fetch(`${API_BASE}${path}`, {
        method,
        headers: buildHeaders(!!body),
        credentials: 'include',
        body: body ? JSON.stringify(body) : undefined,
      });
    } catch {
      // Refresh failed — clear token, will throw 401 below
      localStorage.removeItem('bookscape_token');
    }
  }

  return handleRes(res);
};

// Stream a file download from the API (used for invoice PDF export).
export const downloadFile = async (path, filename) => {
  const token = getToken();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (API_KEY) headers['X-API-Key'] = API_KEY;
  const res = await fetch(`${API_BASE}${path}`, {
    headers,
    credentials: 'include',
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Download failed');
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
